import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, firstValueFrom } from 'rxjs';

import { environment } from 'environments/environment';
import { Service as AlertService } from '@app/services/alert.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class Service {
    private socket!: Socket;
    private Translation: any;

    constructor(
        private TranslateService: TranslateService,
        private AlertService: AlertService,
    ) {
        this.setupSocketConnection();

    }

    async init() {
        this.Translation = await firstValueFrom(this.TranslateService.get(['SUCCESS', 'ERRORS', 'BUTTONS', 'SOCKET']));
    }

    private setupSocketConnection(): void {
        const socketUrl = environment.socket;
        // console.log('Connecting to:', socketUrl);

        this.socket = io(socketUrl, {
            transports: ['polling'],

            auth: {
                token: environment.socket_bearer_token
            },

            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,

            timeout: 10000
        });

        this.socket.on('connect', () => {
            // console.log('Socket connected:', this.socket.id);
            this.AlertService.toast({ type: 'success', title: this.TranslateService.instant('SUCCESS.GOOD_WORK'), message: this.TranslateService.instant('SOCKET.CONNECTION_OK') });
        });

        this.socket.on('disconnect', (reason) => {
            // console.log('Socket disconnected:', reason);
            this.AlertService.toast({ type: 'error', title: this.TranslateService.instant('ERRORS.ERROR'), message: this.TranslateService.instant('SOCKET.DISCONNECTED') });
        });

        this.socket.on('connect_error', (error: any) => {
            console.error('Socket connect error:', error);
            console.error('Socket error message:', error?.message);
            console.error('Socket error description:', error?.description);
            console.error('Socket error context:', error?.context);

            this.AlertService.toast({ type: 'error', title: 'ERRORS.ERROR', message: error?.message || this.TranslateService.instant('SOCKET.CONNECTION_ERROR') });
        });

        this.socket.on('error', (error: any) => {
            console.error('Socket general error:', error);
        });

        this.socket.io.on('reconnect', (attempt) => {
            console.log('Socket reconnected:', attempt);
        });

        this.socket.io.on('reconnect_attempt', (attempt) => {
            console.log('Socket reconnect attempt:', attempt);
        });

        this.socket.io.on('reconnect_error', (error) => {
            console.error('Socket reconnect error:', error);
        });

        this.socket.io.on('reconnect_failed', () => {
            console.error('Socket reconnect failed');
        });
    }

    send(channel: string, room: string, action: string, data: any, except: any = null): void {
        if (!this.socket) {
            console.warn('Socket instance not initialized');
            return;
        }

        if (!this.socket.connected) {
            console.warn('Socket not connected');
            return;
        }

        const payload = { channel, room, action, data, except };
        
        // console.log('Socket send:', payload);
        this.socket.emit('message', payload);
    }

    receive(): Observable<any> {
        return new Observable((observer) => {
            const messageHandler = (socket_data: any) => {
                // console.log('Socket receive:', socket_data);
                
                if (socket_data.channel !== 'ipanda') {
                    return;
                }

                observer.next(socket_data);
            };

            this.socket.on('socket_data', messageHandler);

            return () => {
                this.socket.off('socket_data', messageHandler);
            };
        });
    }

    disconnect(): void {
        if (!this.socket) {
            return;
        }

        console.log('Socket disconnect');

        this.socket.removeAllListeners();
        this.socket.disconnect();
    }

    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    getSocket(): Socket {
        return this.socket;
    }
}